import {
  alertPresetsList,
  alertTransformMapping,
  customAlertPreset,
  emptyAlert,
  groupedPresetOptions,
  priorities,
  sources,
} from "fleet/constant/custom-events";
import camelCase from "lodash/camelCase";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import React from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  FormCheck,
  FormControl,
  FormGroup,
  FormLabel,
  FormText,
  InputGroup,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Creatable } from "react-select";
import { BackButton } from "ui/button/back";
import { NavLoader } from "ui/loader";
import { Notification } from "ui/notification";
import { getErrorMessage } from "util/response";
import * as Options from "util/select";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

class AlertV3FormPage extends React.PureComponent {
  static get propTypes() {
    return {
      alert: PropTypes.object,
      apps: PropTypes.array.isRequired,
      editAlert: PropTypes.func.isRequired,
      loadEventsV3Name: PropTypes.func.isRequired,
      newAlert: PropTypes.func.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      targetOpts: PropTypes.array.isRequired,
      users: PropTypes.array.isRequired,
    };
  }

  constructor(props) {
    super(props);

    const alertState = this._getAlertState(props, {});
    this.state = {
      createResolvableIncident: false,
      emailAddresses: [],
      errorMessage: "",
      eventNameOptions: [],
      eventNameMessage: "",
      preset: null,
      submitted: false,
      successMessage: "",
      targetDevices: [],
      ...alertState,
    };

    this._loadEventNamesSuccessHandler =
      this._loadEventNamesSuccessHandler.bind(this);
    this._loadEventNamesFailureHandler =
      this._loadEventNamesFailureHandler.bind(this);
    this._onCreateResolvableIncidentChange =
      this._onCreateResolvableIncidentChange.bind(this);
    this.getNumberValidation = this.getNumberValidation.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onSelectMulti = this.onSelectMulti.bind(this);
    this.onSelectPreset = this.onSelectPreset.bind(this);
    this._submit = this._submit.bind(this);
    this._createOrEditAlert = this._createOrEditAlert.bind(this);
  }

  componentDidMount() {
    const { loadEventsV3Name } = this.props;

    // On Event Name success, setState will cause rerender.
    loadEventsV3Name()
      .then(this._loadEventNamesSuccessHandler)
      .catch(this._loadEventNamesFailureHandler);
  }

  componentDidUpdate(prevProps, prevState) {
    const updateAlert = this._shouldUpdateAlert(prevProps, prevState);
    if (updateAlert) {
      const alertState = this._getAlertState(this.props, this.state);
      const preset = this._getPresetState(this.props);

      this.setState({
        ...alertState,
        preset,
      });
    }
  }

  _shouldUpdateAlert(prevProps, prevState) {
    const { alert, apps } = this.props;
    const { eventNameOptions } = this.state;

    const alertChanged = !isEqual(alert, prevProps.alert);
    if (alertChanged) {
      return true;
    }

    const appsChanged = !isEqual(apps, prevProps.apps);
    if (appsChanged) {
      return true;
    }

    const eventNameOptionsChanged = !isEqual(
      eventNameOptions,
      prevState.eventNameOptions,
    );
    if (eventNameOptionsChanged) {
      return true;
    }

    return false;
  }

  _loadEventNamesSuccessHandler(resp) {
    const eventNameMessage = get(resp.data, "message");
    const eventNameOptions = resp.data.data.map((en) => ({
      label: en,
      value: en,
    }));

    this.setState({ eventNameOptions, eventNameMessage });
  }

  _loadEventNamesFailureHandler(err) {
    const errorMessage = "Event Names API: " + getErrorMessage(err);

    this.setState({ errorMessage });
  }

  _transformAlert(alert) {
    const transformed = Object.entries(alert).reduce((acc, [k, v]) => {
      const key = alertTransformMapping[k] || k;
      if (key === "notification_only") {
        // A resolvable incident is the inverse of notification only.
        // We can't accomplish this transform by mapping only, as we
        // have to invert the value.
        acc.createResolvableIncident = !v;
      } else {
        acc[camelCase(key)] = v;
      }

      return acc;
    }, {});

    // Transform map of targets into array of targets.
    transformed.targetDevices = Object.keys(transformed.targetDevices).map(
      (t) => t.replace("-", "_"),
    );

    return transformed;
  }

  _getAlertState(props, state) {
    const alert = props.alert ? this._transformAlert(props.alert) : emptyAlert;

    return this._getStateFromAlert(props, state, alert);
  }

  _getPresetState(props) {
    if (props.alert) {
      return {
        label: customAlertPreset.label,
        value: customAlertPreset.label,
      };
    }

    return null;
  }

  _getAlertFromPreset(props, optPreset) {
    const selected = alertPresetsList.find((p) => p.label === optPreset.label);
    const preset = {
      ...customAlertPreset,
      ...selected,
    };

    const name =
      preset.label === customAlertPreset.label ? preset.name : preset.label;
    const sources = preset.sources
      .map((source) => {
        const appId = parseInt(source, 10);
        if (!isNaN(appId)) {
          // It's an app.id source.
          return appId;
        }

        // It's an app.name, get the app.id.
        const app = props.apps.find((a) => a.name === source);

        return app ? app.id : null;
      })
      .filter((s) => s !== null);

    return {
      ...preset,
      name,
      sources,
    };
  }

  _getStateFromAlert(props, state, alert) {
    const { eventNameOptions } = state;
    const { targetOpts, users } = props;

    const getEventNames = Options.getOpt.bind(null, eventNameOptions);
    const eventNames = (alert.eventNames || []).map(getEventNames);

    const sourceOpts = this._getSourceOptions(props);
    const getSources = Options.getOpt.bind(null, sourceOpts);
    const sources = (alert.sources || []).map(getSources);

    const priorityOpts = this._getPriorityOptions();
    const getPriorities = Options.getOpt.bind(null, priorityOpts);
    const priorities = (alert.priorities || []).map(getPriorities);

    const { description = "", frequency, name, threshold } = alert;

    const formState = {
      description,
      eventNames,
      frequency,
      name,
      priorities,
      sources,
      threshold,
    };

    // Don't update when undefined.
    if (alert.createResolvableIncident !== undefined) {
      formState.createResolvableIncident = alert.createResolvableIncident;
    }

    // On preset select don't overwrite targets.
    if (alert.targetDevices) {
      const getTargets = Options.getOpt.bind(null, targetOpts);
      formState.targetDevices = alert.targetDevices.map(getTargets);
    }

    // On preset select don't overwrite users.
    if (alert.userIds) {
      const emailOptions = this._getUserOptions(users);
      const getEmails = Options.getOpt.bind(null, emailOptions);
      formState.emailAddresses = alert.userIds.map(getEmails);
    }

    return formState;
  }

  _submit() {
    this.setState({ submitted: true }, this._createOrEditAlert);
  }

  _createOrEditAlert() {
    const {
      createResolvableIncident,
      eventNames,
      targetDevices,
      frequency,
      name,
      priorities,
      emailAddresses,
      sources,
      threshold,
    } = this.state;

    const missingFields = this._getMissingRequiredFields();
    if (missingFields.length) {
      this.setState({
        errorMessage: `${missingFields.join(", ")} must not be empty.`,
        successMessage: "",
        submitted: false,
      });
      return;
    }

    const numErrFields = this._getInvalidNumberFields();
    if (numErrFields.length) {
      this.setState({
        errorMessage: `${numErrFields.join(", ")} must be a positive number.`,
        successMessage: "",
        submitted: false,
      });
      return;
    }

    const { alert, newAlert, editAlert } = this.props;
    const action = alert ? editAlert : newAlert;
    const submitArgs = {
      id: alert ? alert.id : undefined,
      name,
      eventNames: this._cleanSubmitArgs(eventNames),
      targets: this._cleanSubmitArgs(targetDevices),
      users: this._cleanSubmitArgs(emailAddresses),
      params: {
        notification_only: !createResolvableIncident,
        frequency,
        threshold,
        severity_levels: this._cleanSubmitArgs(priorities),
        sources: this._cleanSubmitArgs(sources),
      },
    };

    return action(submitArgs)
      .then((resp) => {
        this.setState({
          errorMessage: "",
          successMessage: `Your alert was ${alert ? "edited" : "created"}.`,
        });
      })
      .catch((e) => {
        this.setState({
          errorMessage: getErrorMessage(e),
          successMessage: "",
        });
      })
      .finally(() => {
        this.setState({ submitted: false });
      });
  }

  onSelectPreset(opt) {
    const alert = this._getAlertFromPreset(this.props, opt);
    const formState = this._getStateFromAlert(this.props, this.state, alert);

    this.setState({
      preset: opt,
      ...formState,
    });
  }

  onInputChange(key, e) {
    this.setState({ [key]: e.target.value });
  }

  onSelectMulti(key, selected) {
    this.setState({ [key]: selected });
  }

  _onCreateResolvableIncidentChange(e) {
    this.setState({
      createResolvableIncident: e.currentTarget.checked,
    });
  }

  _cleanSubmitArgs(args) {
    return args.map((a) => {
      return Object.prototype.hasOwnProperty.call(a, "value") ? a.value : a;
    });
  }

  _getSourceOptions(props) {
    const { apps } = props;
    const sourceOptions = [
      { label: "Player", value: sources.PLAYER_APP_ID },
      { label: "All applications", value: sources.ALL_APPS_ID },
    ];

    const appOptions = apps.map((a) => {
      return {
        isDisabled: !a.available,
        label: a.name,
        value: a.id,
      };
    });

    return [...sourceOptions, ...appOptions];
  }

  _getPriorityOptions() {
    return Object.keys(priorities).map((p) => {
      return { label: priorities[p].label, value: parseInt(p, 10) };
    });
  }

  _getUserOptions(users) {
    const userOptions = users.map((u) => {
      return {
        label: `${u.name} - ${u.email}`,
        value: u.id,
      };
    });
    return userOptions;
  }

  getNumberValidation(field) {
    const val = this.state[field];
    if (val && val < 0) {
      return "error";
    }
  }

  _getMissingRequiredFields() {
    return ["Name", "Target Devices", "Email Addresses"].filter((f) => {
      const val = this.state[camelCase(f)];
      return !val || (Array.isArray(val) && !val.length);
    });
  }

  _getInvalidNumberFields() {
    return ["Threshold", "Frequency"].filter((f) => {
      return this.getNumberValidation(camelCase(f)) === "error";
    });
  }

  formatAppLabel(sourceOption) {
    const { isDisabled, label } = sourceOption;

    return (
      <span>
        {label}
        <span className="ml-4 font-smaller text-muted text-monospace">
          {isDisabled && "(Unavailable.)"}
        </span>
      </span>
    );
  }

  _renderView() {
    const { alert, storesLoaded, targetOpts, users } = this.props;
    const {
      createResolvableIncident,
      description,
      emailAddresses,
      errorMessage,
      eventNameMessage,
      eventNameOptions,
      eventNames,
      frequency,
      name,
      preset,
      priorities,
      sources,
      submitted,
      successMessage,
      targetDevices,
      threshold,
    } = this.state;
    const successChildren = <Link to="/diagnostics/alerts">View Alerts</Link>;
    const detailsDisabled = !preset;
    const isZeroFrequency = parseInt(frequency, 10) === 0;

    if (!storesLoaded) {
      return <NavLoader.Proxy />;
    }

    return (
      <div>
        <Row className="alert-input-notification">
          <Col xs={6}>
            <Notification
              error={errorMessage}
              success={successMessage}
              successChildren={successChildren}
            />
            <FormGroup controlId="notification">
              <FormLabel>Alert</FormLabel>
              <Select
                name="notification-select"
                isClearable={false}
                onChange={this.onSelectPreset}
                options={groupedPresetOptions}
                placeholder="Select from predefined alerts or create a new one."
                value={preset}
                required
              />
            </FormGroup>
          </Col>
          {description && <Col xs={6}>{description}</Col>}
        </Row>
        <Row>
          <FormGroup>
            <Col xs={12}>
              <FormCheck
                checked={createResolvableIncident}
                label="Also create triggered alerts"
                onChange={this._onCreateResolvableIncidentChange}
                type="checkbox"
              />
            </Col>
            <Col xs={12} sm={6}>
              <FormText>
                <p>
                  If checked, when an alert is created, we will also create a
                  resolvable triggered alert to track issue resolution. The
                  triggered alert will be viewable on Fleet under{" "}
                  <Link to="/diagnostics/incidents">Triggered Alerts</Link>.
                </p>
                <p>Otherwise, we will only send an alert.</p>
              </FormText>
            </Col>
          </FormGroup>
        </Row>
        <Card className="well">
          <Row>
            <Col xs={6}>
              <FormGroup controlId="name">
                <FormLabel>
                  Name <span className="text-danger">*</span>
                </FormLabel>
                <FormControl
                  type="text"
                  disabled={detailsDisabled}
                  onChange={(n) => this.onInputChange("name", n)}
                  placeholder="Enter the name of alert"
                  required
                  value={name}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <FormGroup controlId="event-name" className="w-100">
              <Col xs={6}>
                <FormLabel>Event Name</FormLabel>
                <Creatable
                  name="event-name-select"
                  isDisabled={detailsDisabled || eventNameOptions === null}
                  onChange={(opt) => this.onSelectMulti("eventNames", opt)}
                  options={eventNameOptions}
                  placeholder={
                    eventNameOptions === null
                      ? "Loading..."
                      : "Select or create event name"
                  }
                  value={eventNames}
                  isMulti
                />
              </Col>
              <Col xs={12} />
              {eventNameMessage && !detailsDisabled && (
                <Col xs={6} className="mt-8">
                  <Alert className="alert-archived-content">
                    <i className="fa fa-warning mr-4" />
                    {eventNameMessage}
                  </Alert>
                </Col>
              )}
            </FormGroup>
          </Row>
          <Row>
            <Col xs={6}>
              <FormGroup controlId="source">
                <FormLabel>Source</FormLabel>
                <Select
                  name="source-select"
                  formatOptionLabel={this.formatAppLabel}
                  isDisabled={detailsDisabled}
                  onChange={(opt) => this.onSelectMulti("sources", opt)}
                  options={this._getSourceOptions(this.props)}
                  placeholder="e.g. Application or Player"
                  value={sources}
                  isMulti
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup controlId="priority">
                <FormLabel>Priority</FormLabel>
                <Select
                  name="priority-select"
                  isDisabled={detailsDisabled}
                  onChange={(opt) => this.onSelectMulti("priorities", opt)}
                  options={this._getPriorityOptions()}
                  placeholder="Select Priority"
                  value={priorities}
                  isMulti
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={3}>
              <FormGroup controlId="Frequency">
                <FormLabel>
                  {`Frequency of Event${isZeroFrequency ? "*" : ""}`}
                </FormLabel>
                <FormControl
                  type="number"
                  min="0"
                  disabled={detailsDisabled}
                  placeholder="1"
                  value={frequency}
                  onChange={(f) => this.onInputChange("frequency", f)}
                  isInvalid={this.getNumberValidation("frequency") === "error"}
                />
                <FormControl.Feedback type="invalid">
                  Please enter a valid frequency.
                </FormControl.Feedback>
              </FormGroup>
            </Col>
            <Col xs={3}>
              <FormGroup controlId="Threshold">
                <FormLabel>Threshold</FormLabel>
                <InputGroup className="alert-input-group-threshold">
                  <FormControl
                    type="number"
                    min="1"
                    disabled={detailsDisabled}
                    placeholder="1"
                    value={threshold}
                    onChange={(t) => this.onInputChange("threshold", t)}
                    isInvalid={
                      this.getNumberValidation("threshold") === "error"
                    }
                  />
                  <InputGroup.Text>mins</InputGroup.Text>
                </InputGroup>
                <FormControl.Feedback type="invalid">
                  Please enter a valid threshold (min 1).
                </FormControl.Feedback>
              </FormGroup>
            </Col>
          </Row>
          {isZeroFrequency && (
            <Row>
              <Col xs={12}>
                * Frequency of 0 means alert will be triggered when no event is
                found with the selected criteria.
              </Col>
            </Row>
          )}
        </Card>
        <Row>
          <Col xs={6}>
            <FormGroup controlId="devices">
              <FormLabel>
                Target Devices <span className="text-danger">*</span>
              </FormLabel>
              <Select
                name="devices-select"
                menuPlacement="top"
                onChange={(opt) => this.onSelectMulti("targetDevices", opt)}
                options={targetOpts}
                placeholder="Select from devices, tags or networks"
                value={targetDevices}
                required
                isMulti
              />
            </FormGroup>
          </Col>
          <Col xs={6}>
            <FormGroup controlId="users">
              <FormLabel>
                Email Addresses <span className="text-danger">*</span>
              </FormLabel>
              <Select
                name="users-select"
                menuPlacement="top"
                onChange={(opt) => this.onSelectMulti("emailAddresses", opt)}
                options={this._getUserOptions(users, "id", "email")}
                placeholder="Select from user email addresses"
                value={emailAddresses}
                required
                isMulti
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {this.state.createResolvableIncident && (
              <Alert variant="info">
                <i className="fa fa-info-circle" aria-hidden="true" /> We will
                create an incident when these rules are matched.
              </Alert>
            )}
          </Col>
          <Col xs={12}>
            <BackButton />
            <Button
              variant="primary"
              onClick={this._submit}
              disabled={submitted}
            >
              {alert ? "Update" : "Create"}
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  render() {
    const { alert } = this.props;
    const title = alert ? "Edit Alert" : "New Alert";
    const view = this._renderView();
    return (
      <Page title={title}>
        <Box noHeader={true}>{view}</Box>
      </Page>
    );
  }
}

export { AlertV3FormPage, groupedPresetOptions };
